/**
 * DamagedItemHandlingPage — `/mail/damages`.
 *
 * Ported from `screens/src/pages/mail/DamagedItemHandling.jsx`. Records a
 * DAMAGE transition with severity + notes; lists the recently flagged items.
 *
 * Backend:
 *   - POST /v1/mail-items/:id/transitions with event=DAMAGE.
 *   - GET  /v1/mail-items?status=DAMAGED — filtered list.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listMailItems, transitionMailItem } from '../../lib/api/mail-items';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { MailItemTable } from './shared/MailUI';

const SEVERITY = ['MINOR', 'MAJOR', 'TOTAL_LOSS'] as const;

export function DamagedItemHandlingPage(): JSX.Element {
  const [itemId, setItemId] = useState<string>('');
  const [severity, setSeverity] = useState<string>(SEVERITY[0]);
  const [notes, setNotes] = useState<string>('');
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['mail', 'damaged'],
    queryFn: () => listMailItems({ status: 'DAMAGED', pageSize: 25 }),
    retry: false,
  });

  const mutate = useMutation({
    mutationFn: () =>
      transitionMailItem(itemId.trim(), {
        event: 'DAMAGE',
        notes: `${severity}${notes.trim().length > 0 ? ` — ${notes.trim()}` : ''}`,
      }),
    onSuccess: () => {
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['mail', 'damaged'] });
      void queryClient.invalidateQueries({ queryKey: ['mail', 'items'] });
    },
  });

  const denied = list.isError && list.error instanceof ApiError && (list.error.status === 401 || list.error.status === 403);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Damaged Item Handling"
        description="Flag items damaged in transit. Triggers a claim workflow downstream."
      />

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Record damage
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="text"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            placeholder="Mail item id"
            aria-label="Mail item id"
            className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg"
          />
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            aria-label="Severity"
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
          >
            {SEVERITY.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="What was damaged? Photo references, witnesses…"
          aria-label="Damage notes"
          className="w-full mt-3 px-3 py-2 text-sm border border-border rounded-lg"
        />
        <div className="flex items-center justify-end mt-3 gap-3">
          {mutate.isError ? (
            <span className="text-xs text-red-700">{(mutate.error as Error).message}</span>
          ) : null}
          {mutate.isSuccess ? (
            <span className="text-xs text-nipost-green">Damage recorded.</span>
          ) : null}
          <button
            type="button"
            disabled={itemId.trim().length === 0 || mutate.isPending}
            onClick={() => mutate.mutate()}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {mutate.isPending ? 'Recording…' : 'Record damage'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Recently flagged</h3>
        {list.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            Token lacks <code className="font-mono">Mail/readMailItem</code>.
          </DeferredNotice>
        ) : list.isError ? (
          <DeferredNotice title="Service unreachable">{(list.error as Error).message}</DeferredNotice>
        ) : (
          <MailItemTable items={list.data?.items ?? []} emptyMessage="No damaged items recorded." />
        )}
      </div>
    </div>
  );
}
